"use client";

import { DEMO_CALENDAR_LINK } from "@/lib/constants/booking";
import Testimonial, { productionTestimonials } from "@/app/(public)/shared/components/Testimonial";
import { UnifiedModal, useModalManager } from "@/app/(public)/shared/components/UnifiedModal";
import { Button } from "@/app/(public)/shared/ui/Button";
import { CheckCircle } from "lucide-react";
import Link from "next/link";

const HotelRoomBlocksGraphic = ({ isMobile = false }: { isMobile?: boolean }) => (
  <div
    className={
      isMobile
        ? "w-full max-w-sm mx-auto"
        : "absolute top-[50%] left-[50%] transform -translate-x-1/2 -translate-y-1/2 scale-[0.9] lg:scale-[1.1] origin-center"
    }
  >
    <div
      className={`${isMobile ? "w-full" : "w-[440px]"} bg-white/95 backdrop-blur-sm rounded-2xl p-6 sm:p-8 lg:p-10 shadow-2xl border border-gray-300 max-h-[600px] overflow-y-auto`}
    >
      <div className="flex flex-col gap-6 sm:gap-8">
        <div className="flex flex-col gap-2 sm:gap-3">
          <div className="text-tag-md sm:text-tag-lg text-landing-primary">Hotel Room Block</div>
          <div className="text-body-sm sm:text-body text-landing-secondary">Annual Company Retreat 2026</div>
        </div>

        <div className="flex flex-col gap-4 sm:gap-6">
          <div className="flex flex-col gap-2 sm:gap-3">
            <div className="text-tag-sm sm:text-tag-md text-landing-primary">Hotel</div>
            <div className="h-10 sm:h-12 rounded-lg px-3 sm:px-4 flex items-center border border-landing-light bg-gray-50">
              <div className="text-body-sm sm:text-body text-landing-secondary">Grand Hotel Downtown</div>
            </div>
          </div>

          <div className="flex flex-col gap-2 sm:gap-3">
            <div className="text-tag-sm sm:text-tag-md text-landing-primary">Check-in Date</div>
            <div className="h-10 sm:h-12 rounded-lg px-3 sm:px-4 flex items-center border border-landing-light bg-gray-50">
              <div className="text-body-sm sm:text-body text-landing-secondary">March 15, 2026</div>
            </div>
          </div>

          <div className="flex flex-col gap-2 sm:gap-3">
            <div className="text-tag-sm sm:text-tag-md text-landing-primary">Check-out Date</div>
            <div className="h-10 sm:h-12 rounded-lg px-3 sm:px-4 flex items-center border border-landing-light bg-gray-50">
              <div className="text-body-sm sm:text-body text-landing-secondary">March 18, 2026</div>
            </div>
          </div>

          <div className="flex flex-col gap-2 sm:gap-3">
            <div className="text-tag-sm sm:text-tag-md text-landing-primary">Early Check-in</div>
            <div className="h-10 sm:h-12 rounded-lg px-3 sm:px-4 flex items-center border border-landing-light bg-gray-50">
              <div className="text-body-sm sm:text-body text-landing-secondary">Yes, March 14, 2:00 PM</div>
            </div>
          </div>

          <div className="flex flex-col gap-2 sm:gap-3">
            <div className="text-tag-sm sm:text-tag-md text-landing-primary">Late Check-out</div>
            <div className="h-10 sm:h-12 rounded-lg px-3 sm:px-4 flex items-center border border-landing-light bg-gray-50">
              <div className="text-body-sm sm:text-body text-landing-secondary">Yes, March 19, 2:00 PM</div>
            </div>
          </div>

          <div className="flex flex-col gap-2 sm:gap-3">
            <div className="text-tag-sm sm:text-tag-md text-landing-primary">Special Needs</div>
            <div className="h-10 sm:h-12 rounded-lg px-3 sm:px-4 flex items-center border border-landing-light bg-gray-50">
              <div className="text-body-sm sm:text-body text-landing-secondary">
                Accessibility: Wheelchair accessible
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-2 sm:gap-3">
            <div className="text-tag-sm sm:text-tag-md text-landing-primary">Outside Master Billing Dates</div>
            <div className="h-10 sm:h-12 rounded-lg px-3 sm:px-4 flex items-center border border-landing-light bg-gray-50">
              <div className="text-body-sm sm:text-body text-landing-secondary">March 19-20, 2026</div>
            </div>
          </div>
        </div>

        <div className="pt-2 sm:pt-4">
          <div
            className="w-full h-10 sm:h-12 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: "#5261eb" }}
          >
            <div className="text-white text-tag-sm sm:text-tag-md font-medium">Book Room</div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default function HotelRoomBlocks() {
  const { openModal, closeModal, isModalOpen } = useModalManager();

  return (
    <main>
      <section className="min-h-[calc(100vh-64px)] py-8 sm:py-12 md:py-16 lg:py-8 mt-[64px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
            <div className="flex flex-col gap-8 sm:gap-12 lg:gap-24">
              <div className="flex flex-col gap-4 sm:gap-6">
                <h1 className="text-h2 sm:text-h1 text-landing-primary">Hotel room block</h1>
                <p className="text-body sm:text-body-lg text-landing-secondary max-w-530">
                  Room blocks and guest management made easy
                </p>
                <p className="text-tag-lg sm:text-tag-xl text-landing-primary max-w-530 font-semibold">
                  Everything from sourcing to registration, streamlined.
                </p>
                <p className="text-body-sm sm:text-body text-landing-secondary max-w-530">
                  Increase your booking efficiencies with RSVP and room block on Planned.
                </p>
                <div className="flex gap-3">
                  <Button variant="primary" color="primary" size="md" onClick={() => openModal("demo")}>
                    Talk to our team
                  </Button>
                  <Link href="/create">
                    <Button variant="outline" color="neutral" size="md">
                      Create an Event
                    </Button>
                  </Link>
                </div>
              </div>
            </div>

            <div className="block md:hidden">
              <HotelRoomBlocksGraphic isMobile={true} />
            </div>

            <div className="hidden md:flex items-center justify-center relative bg-landing-secondary rounded-xl overflow-clip h-[500px] lg:h-[600px]">
              <HotelRoomBlocksGraphic />
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-16 md:py-20 lg:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16">
            <h2 className="text-h2 text-landing-primary mb-6">Streamline your hotel room block management</h2>
            <p className="text-body-lg text-landing-secondary max-w-2xl">
              Everything from sourcing to registration, streamlined. Increase your booking efficiencies with RSVP and
              room block on Planned.
            </p>
          </div>

          <div className="space-y-12">
            <div>
              <h3 className="text-h3 text-landing-primary mb-4">Set up hotel room blocks</h3>
              <div className="flex gap-4 items-start">
                <div className="shrink-0 mt-1">
                  <CheckCircle className="w-5 h-5 text-primary" />
                </div>
                <div className="space-y-2">
                  <h4 className="text-body-lg font-semibold text-landing-primary">
                    Block rooms from world-class hotels around the globe with expert negotiators.
                  </h4>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-h3 text-landing-primary mb-4">Track attendance</h3>
              <div className="flex gap-4 items-start">
                <div className="shrink-0 mt-1">
                  <CheckCircle className="w-5 h-5 text-primary" />
                </div>
                <div className="space-y-2">
                  <h4 className="text-body-lg font-semibold text-landing-primary">
                    Review RSVP responses and hotel room bookings in the attendee dashboard.
                  </h4>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-h3 text-landing-primary mb-4">Monitor attrition</h3>
              <div className="flex gap-4 items-start">
                <div className="shrink-0 mt-1">
                  <CheckCircle className="w-5 h-5 text-primary" />
                </div>
                <div className="space-y-2">
                  <h4 className="text-body-lg font-semibold text-landing-primary">
                    Get notified when key deadlines like cutoff dates are coming up to avoid penalties.
                  </h4>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-h3 text-landing-primary mb-4">Collect comprehensive guest preferences</h3>
              <div className="flex gap-4 items-start">
                <div className="shrink-0 mt-1">
                  <CheckCircle className="w-5 h-5 text-primary" />
                </div>
                <div className="space-y-2">
                  <h4 className="text-body-lg font-semibold text-landing-primary">
                    Collect early check-in, late checkout, special needs like accessibility, and bookings outside of
                    master billing dates—all exported into one spreadsheet for easy management.
                  </h4>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Testimonial variant="wall" testimonials={productionTestimonials} title="Don't just take our word for it." />

      <UnifiedModal
        type="cal"
        isOpen={isModalOpen("demo")}
        onClose={() => closeModal("demo")}
        title="Book a Demo"
        subtitle="Schedule a 30-minute demo with our team"
        size="lg"
        calLink={DEMO_CALENDAR_LINK}
        fallbackText="Book your demo with our team"
      />
    </main>
  );
}
